/**
 * The one question-type vocabulary.
 *
 * Four spellings of "fill in the blank" were live at once — the storage enum
 * (`fill_blank`), the pool grid ("Fill in the blank"), the importer
 * ("Fill in the Blank"), and the composer's own list — and each pair was
 * bridged by its own hand-written `Record<string, string>`. Maps written by
 * hand drift: the import-to-pool map sent Fill in the blank to Short Answer
 * and True / False to Multiple Choice, so the same imported question was one
 * type in the library and a different one in the pool.
 *
 * Every conversion now goes through `normalizeQuestionType`, which takes any
 * spelling the app has ever produced and returns the storage id. Surfaces
 * print `questionTypeLabel`, so there is exactly one spelling on screen.
 *
 * **The answer shape is part of the type.** A type does not just name a
 * question, it says what an answer to it looks like: a fill-in item holds
 * accepted strings, not answer choices, so "None of the above" can never be
 * an accepted answer to one. `questionAnswerShape` is what a generator or a
 * form has to honour, and `answerShapeMatchesType` is what a test asserts.
 */

/** Storage enum. The same union as `LibraryItemType`, and the canonical id. */
export type QuestionTypeId =
  | "multiple_choice"
  | "multiple_select"
  | "true_false"
  | "short_answer"
  | "essay"
  | "fill_blank"
  | "matching"
  | "hotspot"

export const QUESTION_TYPE_IDS: readonly QuestionTypeId[] = [
  "multiple_choice",
  "multiple_select",
  "true_false",
  "short_answer",
  "essay",
  "fill_blank",
  "matching",
  "hotspot",
]

/** The spelling every surface prints. Not a second vocabulary — the only one. */
export const QUESTION_TYPE_LABEL: Record<QuestionTypeId, string> = {
  multiple_choice: "Multiple Choice",
  multiple_select: "Multiple Select",
  true_false: "True or False",
  short_answer: "Short Answer",
  essay: "Essay",
  fill_blank: "Fill in the blank",
  matching: "Match the following",
  hotspot: "Hotspot",
}

/**
 * What an answer to this type is made of.
 *
 * - `choices` — a lettered option list with a key (and distractor shares).
 * - `fill_blank` — one accepted-string list per blank in the stem.
 * - `matching` — prompt/match pairs.
 * - `hotspot` — a region on a figure.
 * - `open` — no key; a grader reads it against a rubric.
 */
export type QuestionAnswerShape =
  | "choices"
  | "fill_blank"
  | "matching"
  | "hotspot"
  | "open"

export const QUESTION_TYPE_ANSWER_SHAPE: Record<
  QuestionTypeId,
  QuestionAnswerShape
> = {
  multiple_choice: "choices",
  multiple_select: "choices",
  true_false: "choices",
  short_answer: "open",
  essay: "open",
  fill_blank: "fill_blank",
  matching: "matching",
  hotspot: "hotspot",
}

/** How many options the choice types carry. `null` when the type has none. */
export const QUESTION_TYPE_CHOICE_COUNT: Record<QuestionTypeId, number | null> =
  {
    multiple_choice: 4,
    multiple_select: 5,
    true_false: 2,
    short_answer: null,
    essay: null,
    fill_blank: null,
    matching: null,
    hotspot: null,
  }

/**
 * Every spelling the app has produced, lowercased and whitespace-collapsed.
 *
 * Composer values (`mcq_single`, `numeric`, `ordering`) are here too: the
 * composer offers three shades the bank has no column for, and each one
 * grades like the storage type it is filed as.
 */
const ALIASES: Record<string, QuestionTypeId> = {
  // Storage enum.
  multiple_choice: "multiple_choice",
  multiple_select: "multiple_select",
  true_false: "true_false",
  short_answer: "short_answer",
  essay: "essay",
  fill_blank: "fill_blank",
  matching: "matching",
  hotspot: "hotspot",

  // Pool, paper, and importer wording.
  "multiple choice": "multiple_choice",
  mcq: "multiple_choice",
  "single choice": "multiple_choice",
  "multiple select": "multiple_select",
  "multiple response": "multiple_select",
  "multi-select": "multiple_select",
  "multi select": "multiple_select",
  "true or false": "true_false",
  "true / false": "true_false",
  "true/false": "true_false",
  "short answer": "short_answer",
  "fill in the blank": "fill_blank",
  "fill in the blanks": "fill_blank",
  "match the following": "matching",
  "hot spot": "hotspot",

  // Composer vocabulary.
  mcq_single: "multiple_choice",
  mcq_multiple: "multiple_select",
  numeric: "short_answer",
  ordering: "multiple_choice",
}

/** Any spelling to the storage id. `null` when nothing recognises it. */
export function normalizeQuestionType(
  value: string | null | undefined,
): QuestionTypeId | null {
  if (!value) return null
  const key = value.trim().toLowerCase().replace(/\s+/g, " ")
  return ALIASES[key] ?? null
}

/**
 * Display spelling for any input. Unrecognised values come back as written
 * rather than as a guess: a grid cell that prints "Multiple Choice" over an
 * unknown type has invented a fact about the question.
 */
export function questionTypeLabel(value: string | null | undefined): string {
  const id = normalizeQuestionType(value)
  return id ? QUESTION_TYPE_LABEL[id] : (value ?? "")
}

/**
 * Answer shape for any input. Unknown types are `open`, the only shape that
 * claims nothing about the answer.
 */
export function questionAnswerShape(
  value: string | null | undefined,
): QuestionAnswerShape {
  const id = normalizeQuestionType(value)
  return id ? QUESTION_TYPE_ANSWER_SHAPE[id] : "open"
}

export function questionTypeChoiceCount(
  value: string | null | undefined,
): number | null {
  const id = normalizeQuestionType(value)
  return id ? QUESTION_TYPE_CHOICE_COUNT[id] : null
}

/** True when this type is answered by picking from a list of options. */
export function questionTypeAcceptsChoices(
  value: string | null | undefined,
): boolean {
  return questionAnswerShape(value) === "choices"
}

/** True when this type is answered by typing into blanks in the stem. */
export function questionTypeAcceptsBlanks(
  value: string | null | undefined,
): boolean {
  return questionAnswerShape(value) === "fill_blank"
}

/** True when more than one option can be keyed correct. */
export function questionTypeAllowsMultipleKeys(
  value: string | null | undefined,
): boolean {
  return normalizeQuestionType(value) === "multiple_select"
}

/**
 * The composer's list, which is finer than storage.
 *
 * Numeric and Ordering are real authoring choices with no column of their
 * own in the bank, so each is filed as the storage type it grades like. Both
 * directions live here rather than in two files, because when they lived in
 * two, Multi-select round-tripped through storage as `multiple_choice` while
 * the pool round-tripped it as "Multiple Select" — one question, two types,
 * depending on which way it had last been saved.
 */
export type ComposerQuestionType =
  | "mcq_single"
  | "mcq_multiple"
  | "true_false"
  | "short_answer"
  | "numeric"
  | "essay"
  | "fill_blank"
  | "matching"
  | "ordering"
  | "hotspot"

export const STORAGE_TYPE_BY_COMPOSER_TYPE: Record<
  ComposerQuestionType,
  QuestionTypeId
> = {
  mcq_single: "multiple_choice",
  mcq_multiple: "multiple_select",
  true_false: "true_false",
  short_answer: "short_answer",
  // Graded on what the student typed, like any constructed response.
  numeric: "short_answer",
  essay: "essay",
  fill_blank: "fill_blank",
  matching: "matching",
  // An arrangement, not a single best answer. Filed with the typed responses.
  ordering: "short_answer",
  hotspot: "hotspot",
}

/**
 * Storage back to the composer's list. The lossy pairs (numeric, ordering)
 * resolve to the plain type, so reopening a Numeric item shows Short answer
 * rather than inventing a shade the bank never recorded.
 */
export const COMPOSER_TYPE_BY_STORAGE_TYPE: Record<
  QuestionTypeId,
  ComposerQuestionType
> = {
  multiple_choice: "mcq_single",
  multiple_select: "mcq_multiple",
  true_false: "true_false",
  short_answer: "short_answer",
  essay: "essay",
  fill_blank: "fill_blank",
  matching: "matching",
  hotspot: "hotspot",
}

/** Any spelling, including composer values, to the composer's list. */
export function composerTypeFor(
  value: string | null | undefined,
): ComposerQuestionType {
  const raw = value?.trim().toLowerCase().replace(/\s+/g, " ")
  if (raw && raw in STORAGE_TYPE_BY_COMPOSER_TYPE) {
    return raw as ComposerQuestionType
  }
  const id = normalizeQuestionType(value)
  return id ? COMPOSER_TYPE_BY_STORAGE_TYPE[id] : "mcq_single"
}

/** Composer value to the one display spelling. */
export function labelForComposerType(type: ComposerQuestionType): string {
  return QUESTION_TYPE_LABEL[STORAGE_TYPE_BY_COMPOSER_TYPE[type]]
}

/**
 * Whether an answer model built for a question actually fits its type.
 *
 * The check a generator or an importer has to pass: a fill-in question whose
 * answer arrived as a choice list is not a formatting difference, it is the
 * wrong question.
 */
export function answerShapeMatchesType(
  type: string | null | undefined,
  shape: QuestionAnswerShape,
): boolean {
  return questionAnswerShape(type) === shape
}
